import type { PlanSku } from '@/api/v1/billing/plans'
import { PlanRecurrence, PlanTier } from '@/api/v1/billing/plans'

import { TIER_CONFIG } from './tier-config'

/** Sentinel the API uses on every envelope count that has no ceiling. */
export const UNLIMITED_ENVELOPES = -1

const TIER_RANK: Record<string, number> = {
  [PlanTier.ONE]: 1,
  [PlanTier.PRO]: 2,
  [PlanTier.MAX]: 3,
}

const RECURRENCE_RANK: Record<string, number> = {
  [PlanRecurrence.MONTHLY]: 0,
  [PlanRecurrence.YEARLY]: 1,
}

/**
 * The catalog states yearly allowances as the monthly amount × 12, so every
 * comparison between SKUs of different recurrences happens in monthly terms.
 */
export function toMonthlyVolume(
  envelopesIncluded: number,
  recurrence: string,
): number {
  if (envelopesIncluded === UNLIMITED_ENVELOPES) return UNLIMITED_ENVELOPES
  return recurrence === PlanRecurrence.YEARLY
    ? Math.round(envelopesIncluded / 12)
    : envelopesIncluded
}

export const skuVolume = (sku: PlanSku): number =>
  toMonthlyVolume(sku.envelopesIncluded, sku.recurrence)

export function getSkusForRecurrence(
  skus: Array<PlanSku>,
  recurrence: string,
): Array<PlanSku> {
  return skus.filter((sku) => sku.recurrence === recurrence)
}

const tierSkus = (skus: Array<PlanSku>, tier: string): Array<PlanSku> =>
  skus.filter((sku) => sku.tier === tier)

/** Ascending by monthly allowance, unlimited last. */
const byAllowanceAsc = (a: PlanSku, b: PlanSku): number => {
  const volumeA = skuVolume(a)
  const volumeB = skuVolume(b)
  const rankA = volumeA === UNLIMITED_ENVELOPES ? Infinity : volumeA
  const rankB = volumeB === UNLIMITED_ENVELOPES ? Infinity : volumeB
  return rankA === rankB ? 0 : rankA < rankB ? -1 : 1
}

/**
 * The volume selector's options. Only tiers that actually sell more than one
 * variant contribute buckets — a fixed-volume tier (Enterprise) would
 * otherwise invent a bucket where a single card renders.
 *
 * The guard counts DISTINCT monthly volumes, not SKU rows: a fixed-volume
 * tier has two rows (monthly + yearly) in a full catalog and a row count
 * would stop recognising it as fixed. This makes the function safe whether
 * the caller filters by recurrence first or not.
 */
export function getVolumeBuckets(skus: Array<PlanSku>): Array<number> {
  const buckets = new Set<number>()
  for (const tier of TIER_CONFIG) {
    const volumes = new Set(tierSkus(skus, tier.feature).map(skuVolume))
    if (volumes.size < 2) continue
    for (const volume of volumes) buckets.add(volume)
  }
  return [...buckets].sort((a, b) => {
    if (a === UNLIMITED_ENVELOPES) return 1
    if (b === UNLIMITED_ENVELOPES) return -1
    return a - b
  })
}

/**
 * The SKU a tier's card shows for the selected bucket. A tier that sells a
 * single fixed volume (Enterprise, 200 envelopes) ignores the selector and
 * stays visible on every bucket; a variant tier with no SKU at that bucket
 * has no card at all.
 */
export function resolveTierSku(
  skus: Array<PlanSku>,
  tier: string,
  volume: number,
): PlanSku | null {
  const variants = tierSkus(skus, tier)
  if (variants.length === 0) return null
  if (variants.length === 1) return variants[0]
  return variants.find((sku) => skuVolume(sku) === volume) ?? null
}

/**
 * The SKU a tier's card shows under the covering model. Neutral (null volume,
 * nothing selected) shows the tier's entry variant — the smallest bucket, a
 * fixed-volume tier its only one. A numeric volume shows the cheapest variant
 * whose allowance covers it: the exact bucket, else the next size up
 * (unlimited covers everything, and only unlimited covers an unlimited
 * request). Null when the tier tops out below the request — the card dims at
 * its biggest variant (getLargestTierSku) instead of vanishing.
 */
export function getCoveringTierSku(
  skus: Array<PlanSku>,
  tier: string,
  volume: number | null,
): PlanSku | null {
  const variants = tierSkus(skus, tier).sort(byAllowanceAsc)
  if (variants.length === 0) return null
  if (volume === null) return variants[0]
  return (
    variants.find((sku) => {
      const allowance = skuVolume(sku)
      if (allowance === UNLIMITED_ENVELOPES) return true
      return volume !== UNLIMITED_ENVELOPES && allowance >= volume
    }) ?? null
  )
}

/** The tier's biggest-allowance variant — what a dimmed card still shows. */
export function getLargestTierSku(
  skus: Array<PlanSku>,
  tier: string,
): PlanSku | null {
  const variants = tierSkus(skus, tier).sort(byAllowanceAsc)
  return variants.at(-1) ?? null
}

export const findSkuByCode = (
  skus: Array<PlanSku>,
  code: string | null | undefined,
): PlanSku | undefined =>
  code ? skus.find((sku) => sku.code === code) : undefined

export const coversVolume = (sku: PlanSku, monthlyVolume: number): boolean => {
  const volume = skuVolume(sku)
  return volume === UNLIMITED_ENVELOPES || volume >= monthlyVolume
}

export interface RankableSku {
  tier: string
  recurrence: string
  envelopesIncluded: number
  price: number
}

/**
 * Total order over SKUs, used for both "is this an upgrade" and "which SKU is
 * cheapest for this demand". Order: tier → volume → recurrence → price.
 *
 * Tier and volume dominate recurrence on purpose. The old `isUpgradeAction`
 * compared recurrence FIRST, which made Yearly Starter 30 (R$276/yr) outrank
 * Monthly Enterprise (R$499/mo): the grid rendered "Upgrade" and the
 * confirmation showed `notices.upgrade` ("charged immediately") on what is a
 * hard downgrade. With 3×2 combinations that was a corner; with 7×2 SKUs it is
 * routine, so the carry-over is corrected rather than inherited.
 */
export function compareSkuRank(a: RankableSku, b: RankableSku): number {
  const tier = (TIER_RANK[a.tier] ?? 0) - (TIER_RANK[b.tier] ?? 0)
  if (tier !== 0) return tier

  const volumeA = toMonthlyVolume(a.envelopesIncluded, a.recurrence)
  const volumeB = toMonthlyVolume(b.envelopesIncluded, b.recurrence)
  const rankA = volumeA === UNLIMITED_ENVELOPES ? Infinity : volumeA
  const rankB = volumeB === UNLIMITED_ENVELOPES ? Infinity : volumeB
  if (rankA !== rankB) return rankA < rankB ? -1 : 1

  const recurrence =
    (RECURRENCE_RANK[a.recurrence] ?? 0) - (RECURRENCE_RANK[b.recurrence] ?? 0)
  if (recurrence !== 0) return recurrence

  return a.price - b.price
}

/**
 * Annual discount for the badge on the recurrence selector, derived from the
 * prices actually being shown so it can never disagree with them (retail
 * ~40%, wholesale ~50%). Rounded to the nearest 10 for a clean number.
 * Resolves per tier through the covering model, exactly like the grid, so a
 * neutral (null) volume reads the entry variants.
 */
export function getAnnualSavingsPercent(
  skus: Array<PlanSku>,
  volume: number | null,
): number {
  const monthly = getSkusForRecurrence(skus, PlanRecurrence.MONTHLY)
  const yearly = getSkusForRecurrence(skus, PlanRecurrence.YEARLY)

  const discounts: Array<number> = []
  for (const tier of TIER_CONFIG) {
    const m = getCoveringTierSku(monthly, tier.feature, volume)
    const y = getCoveringTierSku(yearly, tier.feature, volume)
    if (!m || !y || m.price <= 0 || y.price <= 0) continue
    discounts.push((1 - y.price / (m.price * 12)) * 100)
  }

  if (discounts.length === 0) return 40
  return Math.round(Math.max(...discounts) / 10) * 10
}
